import React, { useCallback, useState } from 'react';
import { TrashIcon, PlusIcon, CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import { FieldType } from '@frontend/app/types/Fields';
import { getLabelForFieldType } from '@frontend/app/containers/Communities/AddOrEditCommunity/CommunityIdentity/utils';
import { message, Switch } from '@revfluence/fresh';
import { findIndex } from 'lodash';
import { toLower } from 'lodash';
import { useCommunityIdentityContext } from '@frontend/app/containers/Communities/AddOrEditCommunity/hooks/useCommunityIdentityContext';
import { useSaveMemberFieldSchema } from '@frontend/app/hooks';
import { Button } from '@/shadcn/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/shadcn/components/ui/dialog';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { useRecruitment } from '../RecruitmentContext';
import { Separator } from '@/shadcn/components/ui/separator';
import { IField } from '../../../LandingPages/types';

const SELECT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: FieldType.TEXT, label: getLabelForFieldType(FieldType.TEXT) },
  { value: FieldType.NUMBER, label: getLabelForFieldType(FieldType.NUMBER) },
  { value: FieldType.BOOLEAN, label: getLabelForFieldType(FieldType.BOOLEAN) },
  { value: FieldType.DATE, label: getLabelForFieldType(FieldType.DATE) },
  { value: FieldType.SINGLE_SELECT, label: getLabelForFieldType(FieldType.SINGLE_SELECT) },
  { value: FieldType.MULTI_SELECT, label: getLabelForFieldType(FieldType.MULTI_SELECT) },
];

interface IProps {
  onSaveField?: (field: IField) => void;
}

export const NewFormFieldModal = ({ onSaveField }: IProps) => {
  const {
    showNewFormFieldModal,
    setShowNewFormFieldModal,
  } = useRecruitment();

  const {
    allSchemas,
    customSchemas,
  } = useCommunityIdentityContext() || {};

  const [fieldName, setFieldName] = useState<string>('');

  const [fieldType, setFieldType] = useState<string>(FieldType.TEXT);

  const [options, setOptions] = useState<string[]>([]);

  const isSelectType = fieldType === FieldType.SINGLE_SELECT || fieldType === FieldType.MULTI_SELECT;

  const handleOptionNameChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleFieldTypeChange = (value: string) => {
    setFieldType(value);
    setOptions([]);
  };

  const caseInsensitivePredicateFactory = useCallback((name: string) => {
    name = toLower(name);
    return (schema: typeof allSchemas[0]) => toLower(schema.name) === name;
  }, []);

  const [saveSchema, { loading: isSaving }] = useSaveMemberFieldSchema({
    onCompleted: (data) => {
      message.success('Field created');
      setShowNewFormFieldModal(false);
      setFieldName('');
      setOptions([]);
      setFieldType(FieldType.TEXT);
      onSaveField?.({
        id: data.schema.id,
        name: data.schema.name,
        type: data.schema.type,
        choices: data.schema.choices,
        label: data.schema.name,
        displayLabel: data.schema.name,
        required: false,
        order: null,
        active: true,
        source: 'schema',
        dbName: null
      });
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const handleSaveField = () => {
    if (!fieldName) {
      message.error('You must add a field name.');
      return;
    }

    if (isSelectType && options.length === 0) {
      message.error('You must add at least one option.');
      return;
    }

    if (isSelectType && options.some((option) => !option)) {
      message.error('You must add a name for each option.');
      return;
    }

    const predicate = caseInsensitivePredicateFactory(fieldName);

    const customFieldExists = findIndex(customSchemas, predicate) !== -1;

    if (customFieldExists && !options.length) {
      message.error('This custom property already exists');
      return;
    }

    const existsOnClient = findIndex(allSchemas, predicate) !== -1;

    if (existsOnClient && !options.length) {
      message.error('This field already exists');
      return;
    }

    const payload = {
      id: null,
      name: fieldName,
      type: fieldType,
      choices: options,
    };

    if (fieldType === FieldType.MULTI_SELECT) {
      payload.type = FieldType.ARRAY;
    } else if (fieldType === FieldType.SINGLE_SELECT) {
      payload.type = FieldType.TEXT;
    } else if (!options.length) {
      payload.choices = undefined;
    }

    saveSchema({
      variables: {
        schema: payload,
      },
    });
  };

  return (
    <Dialog open={showNewFormFieldModal} onOpenChange={setShowNewFormFieldModal}>
      <DialogContent className="w-[520px] bg-primary-foreground !rounded-2xl shadow-xl gap-0 shadcn">
        <DialogHeader className="px-6 py-4">
          <DialogTitle className="font-medium text-foreground mb-0 leading-8">New Field</DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="p-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fieldName" className="text-xs font-medium text-foreground">Field Name</Label>
            <Input id="fieldName" placeholder="New Field Name Goes Here" className="text-sm" value={fieldName} onChange={(e) => setFieldName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fieldType" className="text-xs font-medium text-foreground">Field Type</Label>
            <Select value={fieldType} onValueChange={handleFieldTypeChange}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Single Select" />
              </SelectTrigger>
              <SelectContent>
                {SELECT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isSelectType && (
            <div className="flex flex-col gap-4">
              {options.map((option, index) => (
                <div key={index} className="flex flex-col gap-1.5">
                  <Label htmlFor={option} className="text-xs font-medium text-foreground">Option {index + 1}</Label>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Option Name Here" className="flex-1 text-sm" value={option} onChange={(e) => handleOptionNameChange(index, e.target.value)} />
                    <Button
                      variant="outline"
                      size="headerIcon"
                      className="border-destructive text-destructive hover:bg-destructive/10"
                      onClick={() => setOptions(options.filter((o) => o !== option))}
                    >
                      <TrashIcon fontSize={12} />
                    </Button>
                  </div>
                </div>
              ))}
              <Button variant="link" className="p-0 h-auto self-start text-sm font-medium text-foreground hover:text-primary" onClick={() => setOptions([...options, ''])}>
                <PlusIcon className="mr-2" />
                Add option
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Switch id="associateToCampaign" size="small" />
            <Label htmlFor="associateToCampaign" className="text-sm text-foreground flex items-center font-normal">
              Associate field to campaign only
            </Label>
            <CircleInfoIcon fontSize={12} className="text-grey-3" />
          </div>
        </div>
        <Separator />
        <DialogFooter className="px-6 py-4">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button loading={isSaving} onClick={handleSaveField}>Create Field</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
